/**
 * <wild-water>: soft water-caustic light (sunlight through water) drawn with a small WebGL shader
 * on the element's first <canvas>. Used behind the hero (<wild-hero> extends it) and the badges strip.
 *
 * - The pointer sends ripples through the light (data-interactive="true"); when idle, a gentle
 *   ripple drifts on its own.
 * - Colours come from data-base, data-water and data-light. Adds .has-gl once drawing, so the
 *   canvas can fade in over the section's CSS gradient fallback.
 * - Pauses off screen and in hidden tabs. Reduced motion gets a single still frame.
 */
import { prefersReducedMotion, hexToRgb, clamp01 } from '@wild/utils';

const VERTEX = `
attribute vec2 aPosition;
void main() {
  gl_Position = vec4(aPosition, 0.0, 1.0);
}
`;

const FRAGMENT = `
precision mediump float;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uPointer; // xy in 0..1, z = ripple strength
uniform vec3 uBase;
uniform vec3 uWater;
uniform vec3 uLight;

vec2 hash2(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453);
}

// Gap between the nearest and second-nearest moving cell points. It is small along
// cell borders, which is where refracted light gathers into bright caustic lines.
float cellEdge(vec2 x, float t) {
  vec2 n = floor(x);
  vec2 f = fract(x);
  float d1 = 8.0;
  float d2 = 8.0;
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 g = vec2(float(i), float(j));
      vec2 o = hash2(n + g);
      o = 0.5 + 0.42 * sin(t + 6.2831 * o);
      vec2 r = g + o - f;
      float d = dot(r, r);
      if (d < d1) {
        d2 = d1;
        d1 = d;
      } else if (d < d2) {
        d2 = d;
      }
    }
  }
  return sqrt(d2) - sqrt(d1);
}

void main() {
  vec2 frag = gl_FragCoord.xy / uResolution;
  float aspect = uResolution.x / uResolution.y;
  vec2 uv = vec2(frag.x * aspect, frag.y);
  vec2 pointer = vec2(uPointer.x * aspect, uPointer.y);

  // Pointer ripple: rings that bend the light around the cursor.
  vec2 dv = uv - pointer;
  float d = length(dv);
  float ring = sin(d * 42.0 - uTime * 6.0) * exp(-d * 6.0) * uPointer.z;
  uv += (dv / max(d, 0.001)) * ring * 0.015;

  float t = uTime * 0.32;
  vec2 p = uv * 2.4;
  // Two rounds of warping bend the straight cell borders into the wavy lines of real caustics.
  p += vec2(sin(p.y * 1.7 + t * 0.9), cos(p.x * 1.5 - t * 0.8)) * 0.18;
  p += vec2(sin(p.y * 3.4 - t * 0.7), cos(p.x * 3.0 + t * 0.6)) * 0.09;

  float e1 = cellEdge(p, t * 1.4);
  float e2 = cellEdge(p * 1.6 + 7.3, t * 1.1 + 2.0);

  // Slow pools of colour, deeper towards the bottom, like looking down into water.
  float pools = 0.5 + 0.5 * sin(uv.x * 1.3 + t * 0.7) * cos(uv.y * 1.6 - t * 0.5);

  // Soft exponential glow instead of hard edges; brighter where the two layers cross.
  float light = exp(-e1 * 14.0) * 0.7 + exp(-e2 * 20.0) * 0.35;
  light *= 0.65 + 0.35 * pools;
  light += exp(-d * 5.0) * uPointer.z * 0.1;

  float depth = clamp(0.14 + pools * 0.3 + (1.0 - frag.y) * 0.26, 0.0, 1.0);
  vec3 col = mix(uBase, uWater, depth);
  col = mix(col, uLight, clamp(light, 0.0, 1.0) * 0.9);

  // Keep the centre calm behind the headline.
  vec2 c = frag - 0.5;
  float calm = smoothstep(0.6, 0.0, length(c * vec2(1.0, 1.5)));
  col = mix(col, mix(uBase, col, 0.5), calm * 0.5);

  gl_FragColor = vec4(col, 1.0);
}
`;

export class WildWater extends HTMLElement {
  connectedCallback() {
    this.reducedMotion = prefersReducedMotion();
    this.interactive = this.dataset.interactive === 'true' && !this.reducedMotion;
    this.canvas = this.querySelector('canvas');
    this.pointer = { x: 0.7, y: 0.45, tx: 0.7, ty: 0.45, s: 0, ts: 0, lastMove: -Infinity };
    this.running = false;
    this.visible = true;

    if (this.canvas && this.initGL()) {
      this.resize();
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this);

      if (this.reducedMotion) {
        this.draw(12);
      } else {
        this.bindPointer();
        this.observeVisibility();
        this.start();
      }
      this.classList.add('has-gl');
      setTimeout(() => this.classList.add('is-settled'), 2000);
    }
  }

  disconnectedCallback() {
    this.stop();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  initGL() {
    const gl = this.canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      depth: false,
      powerPreference: 'low-power',
      preserveDrawingBuffer: false,
    });
    if (!gl) return false;

    const compile = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      return gl.getShaderParameter(shader, gl.COMPILE_STATUS) ? shader : null;
    };
    const vertex = compile(gl.VERTEX_SHADER, VERTEX);
    const fragment = compile(gl.FRAGMENT_SHADER, FRAGMENT);
    if (!vertex || !fragment) return false;

    const program = gl.createProgram();
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return false;
    gl.useProgram(program);

    // One triangle that covers the whole viewport.
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    this.gl = gl;
    this.uniforms = {
      resolution: gl.getUniformLocation(program, 'uResolution'),
      time: gl.getUniformLocation(program, 'uTime'),
      pointer: gl.getUniformLocation(program, 'uPointer'),
    };
    gl.uniform3fv(gl.getUniformLocation(program, 'uBase'), hexToRgb(this.dataset.base, [0.96, 0.95, 0.93]));
    gl.uniform3fv(gl.getUniformLocation(program, 'uWater'), hexToRgb(this.dataset.water, [0.83, 0.87, 0.89]));
    gl.uniform3fv(gl.getUniformLocation(program, 'uLight'), hexToRgb(this.dataset.light, [1, 1, 1]));

    this.canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      this.stop();
      this.classList.remove('has-gl');
    });
    return true;
  }

  // Render below device resolution: the light is soft, and this keeps phones cool.
  resize() {
    if (!this.gl) return;
    const scale = Math.min(window.devicePixelRatio || 1, 1.5) * 0.55;
    const width = Math.max(1, Math.round(this.clientWidth * scale));
    const height = Math.max(1, Math.round(this.clientHeight * scale));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
      this.gl.viewport(0, 0, width, height);
    }
    if (this.reducedMotion) this.draw(12);
  }

  draw(seconds) {
    const { gl, uniforms, pointer } = this;
    gl.uniform2f(uniforms.resolution, this.canvas.width, this.canvas.height);
    gl.uniform1f(uniforms.time, seconds);
    gl.uniform3f(uniforms.pointer, pointer.x, pointer.y, pointer.s);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  start() {
    if (this.running || !this.visible || document.hidden) return;
    this.running = true;
    const frame = (now) => {
      if (!this.running) return;
      this.tick(now);
      this.frameId = requestAnimationFrame(frame);
    };
    this.frameId = requestAnimationFrame(frame);
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.frameId);
  }

  tick(now) {
    const seconds = now / 1000;
    const p = this.pointer;

    // No pointer for a while: let a soft ripple wander so the water always feels alive.
    if (now - p.lastMove > 2200) {
      p.tx = 0.5 + 0.32 * Math.sin(seconds * 0.21);
      p.ty = 0.45 + 0.22 * Math.cos(seconds * 0.29);
      p.ts = 0.35;
    } else {
      p.ts *= 0.985;
    }
    p.x += (p.tx - p.x) * 0.06;
    p.y += (p.ty - p.y) * 0.06;
    p.s += (p.ts - p.s) * 0.05;

    this.draw(seconds);
  }

  bindPointer() {
    if (!this.interactive) return;
    const toLocal = (event) => {
      const rect = this.getBoundingClientRect();
      return [clamp01((event.clientX - rect.left) / rect.width), clamp01(1 - (event.clientY - rect.top) / rect.height)];
    };
    this.addEventListener('pointermove', (event) => {
      const [x, y] = toLocal(event);
      Object.assign(this.pointer, { tx: x, ty: y, ts: 1, lastMove: performance.now() });
    });
    this.addEventListener('pointerdown', (event) => {
      const [x, y] = toLocal(event);
      Object.assign(this.pointer, { tx: x, ty: y, x, y, s: 1.6, ts: 1.6, lastMove: performance.now() });
    });
  }

  observeVisibility() {
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.visible ? this.start() : this.stop();
    });
    this.intersectionObserver.observe(this);

    this.onVisibility = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.onVisibility);
  }
}

if (!customElements.get('wild-water')) customElements.define('wild-water', WildWater);
